import React, { useState, useMemo } from 'react'
import {
  Box,
  Paper,
  Typography,
  FormControlLabel,
  Checkbox,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { Line } from 'react-chartjs-2'

// Registrar componentes de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
)

const PlanificacionChart = ({ tablaData, mesesColumnas }) => {
  // Tipos de datos disponibles para graficar
  const tiposDatosGraficables = [
    'Inventario Inicial en sitio',
    'Pedido a proveedor',
    'Llegadas (Und)',
    'Llegadas (Cont)',
    'Sell In -- Plan Ventas',
    'Sell Out',
    'Rolling',
    'Inventario final UN (PV) teórico',
    'Inventario final UN (PV) real',
    'Días Inventario objetivo final (PV)',
  ]

  // Estado para controlar qué series se muestran
  const [seriesVisibles, setSeriesVisibles] = useState(
    tiposDatosGraficables.reduce((acc, tipo) => {
      acc[tipo] = ['Sell In -- Plan Ventas', 'Sell Out', 'Inventario final UN (PV) real'].includes(tipo)
      return acc
    }, {})
  )

  // Estado para el tipo de gráfica
  const [tipoGrafica, setTipoGrafica] = useState('line')

  // Colores para las diferentes series
  const coloresSeries = {
    'Inventario Inicial en sitio': '#FF6384',
    'Pedido a proveedor': '#36A2EB',
    'Llegadas (Und)': '#FFCE56',
    'Llegadas (Cont)': '#4BC0C0',
    'Sell In -- Plan Ventas': '#9966FF',
    'Sell Out': '#FF9F40',
    'Rolling': '#FF6384',
    'Inventario final UN (PV) teórico': '#C9CBCF',
    'Inventario final UN (PV) real': '#4BC0C0',
    'Días Inventario objetivo final (PV)': '#36A2EB',
  }

  // Procesar datos para el gráfico
  const datosGrafica = useMemo(() => {
    if (!tablaData || !mesesColumnas || mesesColumnas.length === 0) {
      return {
        labels: [],
        datasets: [],
      }
    }

    // Usar meses como etiquetas del eje X
    const labels = mesesColumnas

    // Crear datasets para cada tipo de dato visible
    const datasets = tiposDatosGraficables
      .filter(tipo => seriesVisibles[tipo])
      .map(tipo => {
        // Encontrar la fila correspondiente a este tipo de dato
        const fila = tablaData.find(row => row.tipoDato === tipo)
        
        if (!fila) {
          return null
        }

        // Extraer valores para cada mes
        const data = mesesColumnas.map(mes => {
          const valor = fila[mes]
          return valor && valor !== '' ? (tipo === 'Días Inventario objetivo final (PV)' ? parseFloat(valor).toFixed(2) : parseInt(valor, 10)) : null
        })

        return {
          label: tipo,
          data,
          borderColor: coloresSeries[tipo],
          backgroundColor: `${coloresSeries[tipo]}20`, // Transparencia del 20%
          tension: 0.1,
          fill: false,
          pointRadius: 4,
          pointHoverRadius: 6,
          connectNulls: false, // No conectar puntos cuando hay valores nulos
        }
      })
      .filter(dataset => dataset !== null)

    return {
      labels,
      datasets,
    }
  }, [tablaData, mesesColumnas, seriesVisibles])

  // Opciones del gráfico
  const opcionesGrafica = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
        },
      },
      title: {
        display: true,
        text: 'Planificación - Valores vs Tiempo',
        font: {
          size: 16,
          weight: 'bold',
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          title(context) {
            return `Período: ${context[0].label}`
          },
          label(context) {
            const valor = context.parsed.y
            return `${context.dataset.label}: ${valor !== null ? valor.toLocaleString() : 'N/A'}`
          },
        },
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Período',
          font: {
            weight: 'bold',
          },
        },
        grid: {
          display: true,
          color: '#e0e0e0',
        },
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Valores',
          font: {
            weight: 'bold',
          },
        },
        grid: {
          display: true,
          color: '#e0e0e0',
        },
        beginAtZero: true,
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
  }

  const handleSerieToggle = (tipo) => {
    setSeriesVisibles(prev => ({
      ...prev,
      [tipo]: !prev[tipo],
    }))
  }


  // Función para seleccionar/deseleccionar todas las series
  const toggleTodasLasSeries = (seleccionar) => {
    setSeriesVisibles(() => {
      const nuevoEstado = {}
      tiposDatosGraficables.forEach(tipo => {
        nuevoEstado[tipo] = seleccionar
      })
      return nuevoEstado
    })
  }

  if (!tablaData || tablaData.length === 0) {
    return (
      <Paper elevation={1} sx={{ p: 3, mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Gráfica de Planificación
        </Typography>
        <Typography color="text.secondary">
          No hay datos disponibles para mostrar en la gráfica
        </Typography>
      </Paper>
    )
  }

  return (
    <Paper elevation={1} sx={{ p: 3, mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Gráfica de Planificación
      </Typography>

      {/* Controles */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            {/* <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Tipo de Gráfica</InputLabel>
              <Select
                value={tipoGrafica}
                label="Tipo de Gráfica"
                onChange={handleTipoGraficaChange}
              >
                <MenuItem value="line">Líneas</MenuItem>
                <MenuItem value="bar">Barras</MenuItem>
              </Select>
            </FormControl> */}
          </Grid>
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => toggleTodasLasSeries(true)}
                style={{
                  padding: '4px 8px',
                  fontSize: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: '#f5f5f5',
                  cursor: 'pointer',
                }}
              >
                Seleccionar Todas
              </button>
              <button
                type="button"
                onClick={() => toggleTodasLasSeries(false)}
                style={{
                  padding: '4px 8px',
                  fontSize: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: '#f5f5f5',
                  cursor: 'pointer',
                }}
              >
                Deseleccionar Todas
              </button>
            </Box>
          </Grid>
        </Grid>

        {/* Filtros de series */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Series a mostrar:
          </Typography>
          <Grid container spacing={1}>
            {tiposDatosGraficables.map((tipo) => (
              <Grid item xs={12} sm={6} md={4} key={tipo}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={seriesVisibles[tipo]}
                      onChange={() => handleSerieToggle(tipo)}
                      size="small"
                      sx={{
                        color: coloresSeries[tipo],
                        '&.Mui-checked': {
                          color: coloresSeries[tipo],
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                      {tipo}
                    </Typography>
                  }
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* Gráfica */}
      <Box sx={{ height: 400, width: '100%' }}>
        <Line data={datosGrafica} options={opcionesGrafica} />
      </Box>
    </Paper>
  )
}

export default PlanificacionChart 